import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  CreateHelpArticleRequest,
  HelpArticle,
  UpdateHelpArticleRequest,
} from '@nucleus-commerce/api-client/admin';

import { helpCenterApi } from '../api/help-center-api';

import { helpCenterKeys } from './help-center-keys';

/** Category counts follow articles, so they refetch with the lists. */
function remember(
  queryClient: ReturnType<typeof useQueryClient>,
  storeId: string,
  row: HelpArticle,
) {
  queryClient.setQueryData(helpCenterKeys.articleDetail(storeId, row.id), row);
  return Promise.all([
    queryClient.invalidateQueries({ queryKey: helpCenterKeys.articleLists(storeId) }),
    queryClient.invalidateQueries({ queryKey: helpCenterKeys.categories(storeId) }),
  ]);
}

/** Field errors (handle taken, unknown category, locale) are rendered by the form, not toasted. */
export function useCreateHelpArticle(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateHelpArticleRequest) => helpCenterApi.createArticle(storeId, body),
    meta: { successMessage: 'helpCenter:toasts.articleCreated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useUpdateHelpArticle(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateHelpArticleRequest }) =>
      helpCenterApi.updateArticle(storeId, id, body),
    meta: { successMessage: 'helpCenter:toasts.articleUpdated', silentError: true },
    onSuccess: (row) => remember(queryClient, storeId, row),
  });
}

export function useDeleteHelpArticle(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => helpCenterApi.removeArticle(storeId, id),
    meta: { successMessage: 'helpCenter:toasts.articleDeleted', silentError: true },
    onSuccess: (_void, id) => {
      queryClient.removeQueries({ queryKey: helpCenterKeys.articleDetail(storeId, id) });
      return Promise.all([
        queryClient.invalidateQueries({ queryKey: helpCenterKeys.articleLists(storeId) }),
        queryClient.invalidateQueries({ queryKey: helpCenterKeys.categories(storeId) }),
      ]);
    },
  });
}
